'use client';

import React, {useState, useEffect, useCallback} from 'react';
import {isError} from '@epic-mw/error-types';
import {useAccountStore, useLocation, useQuickLinks} from '@/hooks';
import {EgsNavigation} from '@wf-components/navigation-react';
import {useRouter} from 'next/navigation';
import {locales} from '@i18n/config';
import {useGlobalClickRouteHandler} from '@/lib/routing';
import {NAV_OWNER} from '@/constants';
import {emitEvent} from '@/lib/utils/analytics';
import {useAccountTotalBalance} from '@/hooks/wallet/wallet.hooks';
import {AccountOfflineInfo} from '@/services/sso/token.types';

import {
	NavLinkClickEvent,
	NavCtaClickEvent,
	NavPropertyLogoClickEvent,
	NavBalanceClickEvent,
	setCookieLocaleHandler
} from '@wf-components/navigation-consumer';
import {genRoute} from '@/lib/utils/route.utils';

interface Props {
	locale: string;
	productName: string;
	offlineAccountInfo: AccountOfflineInfo;
}

function isNavLink(
	e: CustomEvent<NavLinkClickEvent | NavPropertyLogoClickEvent | NavCtaClickEvent>
): e is CustomEvent<NavLinkClickEvent> {
	const event = e as CustomEvent<NavLinkClickEvent>;
	return Boolean(event.detail.link && event.detail.link?.label);
}

export const GlobalNavWrapper = ({locale, productName, offlineAccountInfo}: Props) => {
	const location = useLocation();

	const quickLinks = useQuickLinks(
		[
			'achievements',
			'epicRewards',
			'accountBalance',
			'coupons',
			'account',
			'storeRedeem',
			'fnRedeem',
			'vbucksRedeem',
			'wishlist'
		],
		{productName}
	);

	const onGlobalClick = useGlobalClickRouteHandler<
		NavLinkClickEvent | NavPropertyLogoClickEvent | NavCtaClickEvent | NavBalanceClickEvent
	>(locales, NAV_OWNER);

	const router = useRouter();

	const serverUrl = `${process.env.NEXT_PUBLIC_STORE_URL}`;

	const [accountState, setAccountState] = useState('');

	const {account} = useAccountStore();

	const accountBalance = useAccountTotalBalance();

	useEffect(() => {
		if (window.location.href) {
			try {
				const url = new URL(window.location.href);
				if (url) {
					url.searchParams.append('state', location.pathname);
					url.searchParams.append('lang', locale.replace(/-/, '_'));
					url.searchParams.append('source', NAV_OWNER);
					setAccountState(url.searchParams.toString());
				}
			} catch (e) {
				console.log(
					'Failed to parse href building accountState',
					window.location.href,
					isError(e) && e.message
				);
			}
		}
	}, [location.pathname, locale]);

	const navLinkOnClick = useCallback(
		(e: CustomEvent<NavLinkClickEvent | NavPropertyLogoClickEvent | NavCtaClickEvent>) => {
			const link = e.detail?.link;
			const navLinkEvent: CustomEvent<NavLinkClickEvent> | undefined = isNavLink(e)
				? e
				: undefined;
			const label = navLinkEvent?.detail.link?.label || '';

			emitEvent({
				eventCategory: 'headerNav',
				interactionType: 'click',
				eventAction: 'navigation',
				eventLabel: label || 'property-logo',
				eventValue: {
					component: 'GlobalNavigation',
					section: 'navigation',
					key: link.__typename === 'NavLink' ? link?.key : '',
					owner: link.owner,
					relativePath: link.relativePath
				}
			});

			const isCommon = onGlobalClick(e);
			if (!isCommon) return;

			if (link && link?.owner === NAV_OWNER && link.relativePath) {
				e.detail.originalEvent.preventDefault();
				const p = link.relativePath.startsWith('/')
					? link.relativePath
					: `/${link.relativePath}`;
				router.push(genRoute(p));
			}
		},
		[locale, router, onGlobalClick]
	);

	// const navOnSearch = useCallback(
	// 	(e: CustomEvent<NavSearchEvent>) => {
	// 		if (e.detail.value) {
	// 			router.push(`/${locale}/search?keyword=${e.detail.value}`);
	// 		}
	// 	},
	// 	[locale, router],
	// );

	const ctaOnClick = useCallback(
		(e: CustomEvent<NavCtaClickEvent>) => {
			const link = e.detail?.link;

			emitEvent({
				eventCategory: 'headerNav',
				interactionType: 'click',
				eventAction: 'navigation',
				eventLabel: link.label || link.relativePath || link.hrefTemplate || '',
				eventValue: {
					component: 'GlobalNavigation',
					section: 'navigation',
					key: link.key,
					owner: link.owner,
					relativePath: link.relativePath
				}
			});

			const isCommon = onGlobalClick(e);
			if (!isCommon) return;

			if (e.detail) {
				e.detail.originalEvent?.preventDefault();
				const link = e.detail?.link;
				const base = link.relativePath || '/download';
				const p = base.startsWith('/') ? base : `/${base}`;
				router.push(genRoute(`${p}?start=true`));
			}
		},
		[locale, router, onGlobalClick]
	);

	const walletBalanceOnClick = useCallback(
		(e: CustomEvent<NavBalanceClickEvent>) => {
			const link = e.detail?.link;

			emitEvent({
				eventCategory: 'headerNav',
				interactionType: 'click',
				eventAction: 'navigation',
				eventLabel: 'account-balance',
				eventValue: {
					component: 'GlobalNavigation',
					section: 'navigation',
					owner: NAV_OWNER,
					from: location.url.toString(),
					to: quickLinks.accountBalance,
					...link
				}
			});

			const isCommon = onGlobalClick(e);
			if (!isCommon) return;
		},
		[location.pathname, locale, router, onGlobalClick]
	);

	const getAuthLinkHref = (type: 'login' | 'logout') => {
		return `${process.env.NEXT_PUBLIC_APP_CONTEXT}/api/id/${type}${accountState ? `?${accountState}` : ''}`;
	};

	return (
		<EgsNavigation
			sticky
			disableSearch
			disableCtaButtonOnMobile
			searchActivated={undefined}
			activeSearch={false}
			domain={serverUrl}
			locale={locale}
			localeOnClick={setCookieLocaleHandler}
			isLoggedIn={offlineAccountInfo.isValidSession || account?.isLoggedIn || false}
			displayName={
				offlineAccountInfo.oauthSession?.displayName || account?.displayName || undefined
			}
			propLogoOnClick={navLinkOnClick}
			navLinkOnClick={navLinkOnClick}
			// navOnSearch={navOnSearch}
			ctaOnClick={ctaOnClick}
			signInLinkHref={getAuthLinkHref('login')}
			signOutLinkHref={getAuthLinkHref('logout')}
			skipNavContentId="fgc-app"
			walletBalance={
				!accountBalance.isLoading && Number(accountBalance.data?.totalBalance) > 0
					? accountBalance.data?.formattedBalance
					: undefined
			}
			walletBalanceHref={quickLinks.accountBalance}
			walletBalanceOnClick={walletBalanceOnClick}
		/>
	);
};
